<script lang="ts" setup>
import type { JobFilterCustomDataProperty, JobFilterProperty } from '@ironsight/types'
import { computed, PropType } from 'vue'
import { isLocationFilter, useFilterChipLabel } from './useFilterChips'
import { useFilterContextBridge } from '@/components/filters/useFilterContextBridge'

const MAX_TOOLTIP_ITEMS = 20

const props = defineProps({
  chipId: {
    type: String as PropType<string>,
    required: true,
  },
  filterableProperties: {
    type: Array as PropType<JobFilterProperty[]>,
    required: true,
  },
})

const { store } = useFilterContextBridge()
const { getVisibleFilters } = store

const emit = defineEmits<{
  (e: 'toggle', id: string): void
  (e: 'remove', id: string): void
}>()

const handleClick = (event: MouseEvent) => {
  event.stopPropagation()
  emit('toggle', props.chipId)
}

const handleClose = () => {
  emit('remove', props.chipId)
}

const filter = computed(() => {
  return getVisibleFilters.value.find((f) => f.id === props.chipId)
})

const property = computed(() => {
  if (!filter.value) return null

  if (filter.value.property === 'CustomData') {
    return props.filterableProperties.find(
      (prop) =>
        (prop as JobFilterCustomDataProperty).key &&
        (prop as JobFilterCustomDataProperty).key === filter.value?.key,
    ) as JobFilterCustomDataProperty | undefined
  } else {
    return props.filterableProperties.find((prop) => prop.name === filter.value?.property) as
      | JobFilterProperty
      | undefined
  }
})

const { fullLabel, truncatedLabel } = useFilterChipLabel(filter, property)

const shouldShowTooltip = computed(() => {
  const itemCount = Array.isArray(filter.value?.value) ? filter.value.value.length : 0

  return (
    itemCount <= MAX_TOOLTIP_ITEMS &&
    (truncatedLabel.value !== fullLabel.value || (filter.value && isLocationFilter(filter.value)))
  )
})
</script>

<template>
  <v-tooltip v-if="shouldShowTooltip" bottom>
    <template #activator="{ on }">
      <div v-on="on">
        <v-chip
          class="v-chip--selected"
          dense
          close
          @click="handleClick"
          @click:close="handleClose"
        >
          {{ truncatedLabel }}
        </v-chip>
      </div>
    </template>
    <span>{{ fullLabel }}</span>
  </v-tooltip>
  <v-chip
    v-else
    class="v-chip--selected"
    close
    dense
    @click="handleClick"
    @click:close="handleClose"
  >
    {{ truncatedLabel }}
  </v-chip>
</template>

<style scoped>
:deep(.v-btn.v-btn--icon) {
  width: 18px;
  height: 18px;
}
</style>
